import { useLocation } from 'react-router';

import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import {
    professionalCityLinks,
    professionalLocationHref,
} from '@modules/professional/util/professional-city-links';
import { professionalTabBase } from '@modules/professional/util/professional-tab-base';
import { DetailCreditsRow } from '~/ui-rocco/pages/detail-shared';

/**
 * The product page's meta row, on the profile: the practice's services as
 * the tags, then the cities they work from and the bodies they belong to as
 * the two columns of names. Each hides when empty; the row hides when all
 * three are.
 */
export const ProfessionalMetaRow = () => {
    const initial = useProfessionalInitialRouteData();
    const { pathname } = useLocation();
    if (!initial) return null;

    const tabBase = professionalTabBase(initial.URLSegment, pathname);

    const services = Array.from(
        new Set(
            (initial.Services ?? [])
                .map((service) => service.trim())
                .filter((service) => service !== ''),
        ),
    ).map((label) => ({ label }));

    // A city opens the contact tab on its branch, the way a pin on that map
    // does — the names were plain text, so the map was the only way in.
    const locations = professionalCityLinks(initial.LibraryLocations).map(
        ({ city, locationID }) => ({
            name: city,
            link: professionalLocationHref(tabBase, locationID),
        }),
    );

    // Deduped on name, keeping the first segment seen: an association is a
    // professional in its own right, so its name points at its profile.
    const associations = Array.from(
        (initial.Associations ?? [])
            .reduce((byName, association) => {
                const name = association.Title?.trim();
                if (!name || byName.has(name)) return byName;
                byName.set(name, {
                    name,
                    link: association.URLSegment
                        ? `/professional/${association.URLSegment}`
                        : undefined,
                });
                return byName;
            }, new Map<string, { name: string; link?: string }>())
            .values(),
    );

    return (
        <DetailCreditsRow
            tags={services}
            columns={[
                {
                    label: 'Locations',
                    entries: locations,
                    columns: locations.length > 5 ? 2 : 1,
                },
                { label: 'Associations', entries: associations },
            ]}
        />
    );
};
